import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BlockedCreatorItem } from "@unbox/api";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// ── Query key builders ────────────────────────────────────────────────────────

const blockedListKey = (
  merchantId: string,
  workspaceId: string | null | undefined,
  search: string,
  offset: number,
  limit: number,
) => ["blocked-creators", merchantId, workspaceId, search, offset, limit] as const;

const blockedCountKey = (
  merchantId: string,
  workspaceId: string | null | undefined,
  search: string,
  startDate?: string,
  endDate?: string,
) => ["blocked-creators-count", merchantId, workspaceId, search, startDate, endDate] as const;

const blocklistRulesCountKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["blocklist-rules-count", merchantId, workspaceId] as const;

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useBlockedCreatorsList = ({
  search = "",
  offset = 0,
  limit = 20,
}: {
  search?: string;
  offset?: number;
  limit?: number;
} = {}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: blockedListKey(scope.merchantId, scope.workspaceId, search, offset, limit),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.$get({
            param: scope.param,
            query: { offset: String(offset), limit: String(limit), search: search || undefined },
          })
        : await client.merchant[":merchantId"].blocklist.$get({
            param: scope.param,
            query: { offset: String(offset), limit: String(limit), search: search || undefined },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useBlockedCreatorsCount = ({
  search = "",
  startDate,
  endDate,
  enabled = true,
}: {
  search?: string;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
} = {}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: blockedCountKey(scope.merchantId, scope.workspaceId, search, startDate, endDate),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.count.$get({
            param: scope.param,
            query: { search: search || undefined, startDate, endDate },
          })
        : await client.merchant[":merchantId"].blocklist.count.$get({
            param: scope.param,
            query: { search: search || undefined, startDate, endDate },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: enabled && !!scope.merchantId,
  });
};

export const useBlocklistRulesCount = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: blocklistRulesCountKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.rules.count.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].blocklist.rules.count.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

// ── Plain fetchers (for useCrossPageSelection / id fetching) ─────────────────
// IMPORTANT: query keys here must stay byte-for-byte identical to blockedListKey
// and blockedCountKey above — a mismatch causes cache misses.

const fetchBlockedList = async (
  merchantId: string,
  workspaceId: string | null | undefined,
  param: Record<string, string>,
  search: string,
  offset: number,
  limit: number,
) => {
  const res = workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.$get({
        param: param as { workspaceId: string; merchantId: string },
        query: { offset: String(offset), limit: String(limit), search: search || undefined },
      })
    : await client.merchant[":merchantId"].blocklist.$get({
        param: param as { merchantId: string },
        query: { offset: String(offset), limit: String(limit), search: search || undefined },
      });
  if (!res.ok) throw new Error("Failed to fetch blocked creators");
  return res.json();
};

const fetchBlockedCount = async (
  merchantId: string,
  workspaceId: string | null | undefined,
  param: Record<string, string>,
  search: string,
) => {
  const res = workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.count.$get({
        param: param as { workspaceId: string; merchantId: string },
        query: { search: search || undefined },
      })
    : await client.merchant[":merchantId"].blocklist.count.$get({
        param: param as { merchantId: string },
        query: { search: search || undefined },
      });
  if (!res.ok) throw new Error("Failed to fetch blocked creators count");
  return res.json();
};

export const useBlockedCreatorRowsFetcher = (search = "") => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<BlockedCreatorItem>> => {
      const [listData, countData] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: blockedListKey(scope.merchantId, scope.workspaceId, search, offset, limit),
          queryFn: () => fetchBlockedList(scope.merchantId, scope.workspaceId, scope.param, search, offset, limit),
          staleTime: Number.POSITIVE_INFINITY,
        }),
        queryClient.fetchQuery({
          queryKey: blockedCountKey(scope.merchantId, scope.workspaceId, search),
          queryFn: () => fetchBlockedCount(scope.merchantId, scope.workspaceId, scope.param, search),
          staleTime: Number.POSITIVE_INFINITY,
        }),
      ]);
      return { items: listData.items, total: countData.total };
    },
    [queryClient, scope.merchantId, scope.workspaceId, scope.param, search],
  );
};

// ── Invalidation helper ───────────────────────────────────────────────────────

export const invalidateBlocklist = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["creator-overview"] });
  queryClient.invalidateQueries({ queryKey: ["creators-count"] });
  queryClient.invalidateQueries({ queryKey: ["creator"] });
  queryClient.invalidateQueries({ queryKey: ["blocked-creators"] });
  queryClient.invalidateQueries({ queryKey: ["blocked-creators-count"] });
  queryClient.invalidateQueries({ queryKey: ["candidates"] });
  queryClient.invalidateQueries({ queryKey: ["blocklist-exemptions"] });
  queryClient.invalidateQueries({ queryKey: ["blocklist-rules"] });
};

export const useBlockCreator = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.$post({
            param: scope.param,
            json: { creatorOpenId },
          })
        : await client.merchant[":merchantId"].blocklist.$post({
            param: scope.param,
            json: { creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateBlocklist(queryClient),
  });
};

export const useBlockCreators = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenIds: string[]) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.bulk.$post({
            param: scope.param,
            json: { creatorOpenIds },
          })
        : await client.merchant[":merchantId"].blocklist.bulk.$post({
            param: scope.param,
            json: { creatorOpenIds },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateBlocklist(queryClient),
  });
};

export const useUnblockCreator = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist[":creatorOpenId"].$delete({
            param: { ...scope.param, creatorOpenId },
          })
        : await client.merchant[":merchantId"].blocklist[":creatorOpenId"].$delete({
            param: { ...scope.param, creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateBlocklist(queryClient),
  });
};

const UNBLOCK_CHUNK_SIZE = 2000;

export const useUnblockCreators = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creatorOpenIds, withExemption }: { creatorOpenIds: string[]; withExemption: boolean }) => {
      let unblocked = 0;
      for (let i = 0; i < creatorOpenIds.length; i += UNBLOCK_CHUNK_SIZE) {
        const json = { creatorOpenIds: creatorOpenIds.slice(i, i + UNBLOCK_CHUNK_SIZE), withExemption };
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist["bulk-unblock"].$post({
              param: scope.param,
              json,
            })
          : await client.merchant[":merchantId"].blocklist["bulk-unblock"].$post({ param: scope.param, json });
        if (!res.ok) throw await handleError(res);
        unblocked += (await res.json()).unblocked;
      }
      return { unblocked };
    },
    onSuccess: () => invalidateBlocklist(queryClient),
  });
};

export const useExemptCreator = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist[":creatorOpenId"].exempt.$post({
            param: { ...scope.param, creatorOpenId },
          })
        : await client.merchant[":merchantId"].blocklist[":creatorOpenId"].exempt.$post({
            param: { ...scope.param, creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateBlocklist(queryClient),
  });
};

export const useDownloadBlocklistImportTemplate = () => {
  const scope = useScopedMerchantClient();

  return async () => {
    const res = scope.workspaceId
      ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.import.template.$get({
          param: scope.param,
        })
      : await client.merchant[":merchantId"].blocklist.import.template.$get({ param: scope.param });
    if (!res.ok) throw await handleError(res);

    const blob = await res.blob();
    const filename =
      res.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "blocklist-import-template.csv";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
};

export const useBlocklistImportPreview = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (csv: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.import.preview.$post({
            param: scope.param,
            json: { csv },
          })
        : await client.merchant[":merchantId"].blocklist.import.preview.$post({
            param: scope.param,
            json: { csv },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
  });
};

export type BlocklistImportCommitResult =
  | { async: false; blocked: number; failed: number }
  | { async: true; jobId: string };

export const useBlocklistImportCommit = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenIds: string[]): Promise<BlocklistImportCommitResult> => {
      const rows = creatorOpenIds.map((creatorOpenId) => ({ creatorOpenId }));
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].blocklist.import.commit.$post({
            param: scope.param,
            json: { rows },
          })
        : await client.merchant[":merchantId"].blocklist.import.commit.$post({
            param: scope.param,
            json: { rows },
          });
      if (!res.ok) throw await handleError(res);
      const data = await res.json();
      if (res.status === 202 && "async" in data && data.async) {
        return { async: true, jobId: data.jobId };
      }
      const syncData = data as { blocked: number; failed: number };
      return { async: false, blocked: syncData.blocked, failed: syncData.failed };
    },
    onSuccess: (result) => {
      // The async job's completion is invalidated later by BulkActionProgressProvider
      // once the SSE completion event arrives — the table hasn't actually changed yet.
      if (!result.async) invalidateBlocklist(queryClient);
    },
  });
};
